import AccountSelect from "../components/AccountSelect";
import SubscriptionPaymentHistory from "../components/SubscriptionPaymentHistory";
import { subscriptionBillingDate } from '../lib/subscriptionBillingDate';
import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "@/lib/toast";
import { Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CurrencyInput } from "../components/ui/currency-input";
import { formatMoney } from "@/lib/currency";

/* ─── Helpers ─── */
const fmt = (amount, currency = "COP") =>
  formatMoney(amount, currency);

function formatPaymentDate(dateStr) {
  if (!dateStr) return null;
  const d = subscriptionBillingDate(dateStr);
  if (!d) return null;
  if (isNaN(d.getTime())) return null;
  let formatted = d.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  formatted = formatted.replace(/,/g, "");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  const d = subscriptionBillingDate(dateStr);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const d = subscriptionBillingDate(dateStr);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (d - today) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 3;
}

function isPaymentPending(dateStr) {
  if (!dateStr) return true;
  const dueDate = subscriptionBillingDate(dateStr);
  if (!dueDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate <= today;
}

/* ─── Known service icons ─── */
function getServiceLogo(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("netflix")) return { type: "text", content: "N", color: "#E50914", bg: "#E1E9FC" };
  if (n.includes("spotify")) return { type: "svg", content: "spotify", bg: "#E1E9FC" };
  if (n.includes("disney")) return { type: "text", content: "Disney+", color: "#01147C", bg: "#E1E9FC", fontSize: 11, fontWeight: 800 };
  if (n.includes("youtube")) return { type: "text", content: "▶", color: "#FF0000", bg: "#E1E9FC" };
  if (n.includes("smart") || n.includes("gym") || n.includes("fit")) return { type: "text", content: "🏋️", bg: "#E1E9FC" };
  if (n.includes("amazon") || n.includes("prime")) return { type: "text", content: "📦", bg: "#E1E9FC" };
  if (n.includes("apple")) return { type: "text", content: "🍎", bg: "#E1E9FC" };
  if (n.includes("hbo") || n.includes("max")) return { type: "text", content: "HBO", color: "#5822B4", bg: "#E1E9FC", fontSize: 13, fontWeight: 900 };
  return null;
}

function ServiceIcon({ sub }) {
  const known = getServiceLogo(sub.name);
  const icon = sub.icon;

  return (
    <div style={{
      width: 68, height: 68, borderRadius: "50%",
      background: known?.bg || "#E1E9FC",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, overflow: "hidden",
    }}>
      {known?.type === "text" ? (
        <span style={{
          fontFamily: "'Manrope', sans-serif",
          fontWeight: known.fontWeight || 900,
          fontSize: known.fontSize || 28,
          color: known.color || "#4A4A4A",
        }}>{known.content}</span>
      ) : known?.type === "svg" && known.content === "spotify" ? (
        <svg width="34" height="34" viewBox="0 0 24 24" fill="#1DB954">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424c-.18.295-.565.387-.86.207-2.377-1.454-5.37-1.783-8.893-.982-.336.075-.668-.135-.744-.47-.077-.337.135-.668.47-.745 3.856-.88 7.15-.5 9.822 1.135.296.18.387.563.205.855zm1.224-2.724c-.226.367-.707.487-1.074.26-2.72-1.672-6.87-2.157-10.076-1.182-.413.125-.847-.107-.972-.52-.125-.413.108-.847.52-.973 3.666-1.112 8.24-.57 11.34 1.342.368.228.488.708.262 1.073zm.106-2.833C14.492 8.76 8.707 8.567 5.367 9.58c-.524.16-1.077-.14-1.237-.665-.16-.525.14-1.078.665-1.238 3.83-1.162 10.222-.94 14.185 1.412.473.28.627.893.346 1.365-.28.472-.893.626-1.365.346z"/>
        </svg>
      ) : icon && typeof icon === "string" && icon.startsWith("/") ? (
        <img src={icon} alt="icono" style={{ width: 44, height: 44, objectFit: "contain" }} />
      ) : icon && typeof icon === "string" && icon.length <= 4 ? (
        <span style={{ fontSize: 30 }}>{icon}</span>
      ) : (
        <span style={{ fontSize: 30 }}>📱</span>
      )}
    </div>
  );
}

/* ─── Main Component ─── */
const SubscriptionsPage = () => {
  const { user } = useAuth();
  const { isExpired } = useOutletContext() || {};
  const userCurrency = user?.currency || "COP";

  const [subscriptions, setSubscriptions] = useState([]);
  const [historySub, setHistorySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setPaymentAvailabilityTick] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [formData, setFormData] = useState({
    name: "", price: "", billing_cycle: "monthly", icon: "🎬", next_billing: "", currency: userCurrency
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Payment modal state
  const [paymentModalSub, setPaymentModalSub] = useState(null);
  const [paymentData, setPaymentData] = useState({ amount: "", description: "", currency: userCurrency });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentCards, setPaymentCards] = useState([]);
  const [paymentCardsLoading, setPaymentCardsLoading] = useState(false);
  const [paymentCardsError, setPaymentCardsError] = useState(false);

  useEffect(() => {
    if (!paymentModalSub) return;
    let active = true;
    setPaymentCardsLoading(true);
    setPaymentCardsError(false);
    api.get("/credit-cards").then(({ data }) => {
      if (active) setPaymentCards(data);
    }).catch(() => { if (active) setPaymentCardsError(true); })
      .finally(() => { if (active) setPaymentCardsLoading(false); });
    return () => { active = false; };
  }, [paymentModalSub]);

  useEffect(() => { fetchSubscriptions(); }, []);

  // Refresh the due-date checks while the dashboard is open, so the payment
  // button returns as soon as the following billing date is reached.
  useEffect(() => {
    const intervalId = window.setInterval(() => setPaymentAvailabilityTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get("/subscriptions");
      setSubscriptions(res.data);
    } catch { toast.error("Error al cargar suscripciones"); }
    finally { setLoading(false); }
  };

  const openAddDialog = () => {
    setEditingSub(null);
    setFormData({ name: "", price: "", billing_cycle: "monthly", icon: "🎬", next_billing: "", currency: userCurrency });
    setDialogOpen(true);
  };

  const openEditDialog = (sub) => {
    setEditingSub(sub);
    setFormData({
      name: sub.name, price: sub.price ? String(sub.price) : "",
      billing_cycle: sub.billing_cycle || "monthly", icon: sub.icon || "🎬",
      next_billing: sub.next_billing ? sub.next_billing.substring(0, 10) : "",
      currency: sub.currency || userCurrency
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) { toast.error("Completa los campos requeridos"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        next_billing: formData.next_billing ? new Date(formData.next_billing + "T12:00:00").toISOString() : null
      };
      if (editingSub) {
        await api.put(`/subscriptions/${editingSub.id}`, payload);
        toast.success("Suscripción actualizada");
      } else {
        await api.post("/subscriptions", payload);
        toast.success("Suscripción registrada");
      }
      setDialogOpen(false);
      fetchSubscriptions();
    } catch { toast.error(editingSub ? "Error al actualizar" : "Error al crear suscripción"); }
    finally { setSubmitting(false); }
  };

  const openPaymentModal = (sub) => {
    if (!isPaymentPending(sub.next_billing)) return;
    setPaymentModalSub(sub);
    setPaymentData({
      amount: sub.price ? String(sub.price) : "",
      description: `Pago ${sub.name}`,
      currency: sub.currency || userCurrency,
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    const rawAmount = parseFloat(paymentData.amount);
    if (!rawAmount || isNaN(rawAmount)) { toast.error("Ingresa un monto válido"); return; }
    if (paymentData.funding_type === "credit" && (paymentCardsLoading || paymentCardsError || !paymentData.payment_method)) {
      toast.error("Selecciona la tarjeta de crédito utilizada"); return;
    }
    setPaymentSubmitting(true);
    try {
      await api.post(`/subscriptions/${paymentModalSub.id}/payments`, {
        account_id: paymentData.funding_type === "credit" ? null : paymentData.account_id || "unassigned",
        payment_method: paymentData.funding_type === "credit" ? paymentData.payment_method : "cash",
        amount: rawAmount,
        description: paymentData.description || `Pago ${paymentModalSub.name}`,
      });

      toast.success("Pago registrado en movimientos ✅");
      setPaymentModalSub(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar el pago");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/subscriptions/${id}`);
      toast.success("Suscripción eliminada");
      setDialogOpen(false);
      setEditingSub(null);
      fetchSubscriptions();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleToggleReminder = async (sub) => {
    const newVal = !sub.whatsapp_reminder;
    // Optimistic update
    setSubscriptions(prev => prev.map(s => s.id === sub.id ? { ...s, whatsapp_reminder: newVal } : s));
    try {
      await api.put(`/subscriptions/${sub.id}`, {
        name: sub.name,
        price: sub.price,
        billing_cycle: sub.billing_cycle,
        icon: sub.icon,
        next_billing: sub.next_billing,
        currency: sub.currency,
        whatsapp_reminder: newVal,
      });
    } catch {
      // Revert on error
      setSubscriptions(prev => prev.map(s => s.id === sub.id ? { ...s, whatsapp_reminder: !newVal } : s));
      toast.error("Error al actualizar recordatorio");
    }
  };

  const totalMonthly = subscriptions.reduce((sum, s) => {
    const p = s.price || 0;
    return sum + (s.billing_cycle === "yearly" ? p / 12 : p);
  }, 0);

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div style={{ display: "flex", gap: 20, padding: "20px 0", flexWrap: "wrap" }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ flex: 1, minWidth: 180, height: 160, borderRadius: 20, background: "#e9ecef", animation: "pulse 1.5s infinite" }} />
        ))}
      </div>
    );
  }

  /* ─── Render ─── */
  return (
    <div data-testid="subscriptions-page" style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}>

      {/* ══════ TITLE ══════ */}
      <h1 style={{
        fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 24,
        lineHeight: "33px", color: "#255E99", margin: "0 0 24px 0",
      }}>Mis suscripciones</h1>

      {subscriptions.length === 0 ? (
        <div style={{
          width: "100%",
          background: "#FFFFFF",
          boxShadow: "0px 10px 30px rgba(176, 179, 189, 0.15)",
          borderRadius: "20px",
          padding: "80px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}>
          {/* Custom vector shopping bag empty state illustration */}
          <svg width="400" height="200" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Background Clouds/Pills */}
            <rect x="100" y="80" width="200" height="24" rx="12" fill="#F3F7FF" />
            <rect x="130" y="55" width="140" height="24" rx="12" fill="#F3F7FF" />
            <rect x="70" y="105" width="260" height="24" rx="12" fill="#F3F7FF" />
            <circle cx="330" cy="130" r="12" fill="#E7F6FF" />

            {/* Bag shining rays */}
            <line x1="175" y1="40" x2="185" y2="50" stroke="#1F64E7" strokeWidth="4" strokeLinecap="round" />
            <line x1="200" y1="35" x2="200" y2="47" stroke="#1F64E7" strokeWidth="4" strokeLinecap="round" />
            <line x1="225" y1="40" x2="215" y2="50" stroke="#1F64E7" strokeWidth="4" strokeLinecap="round" />

            {/* Shopping Bag */}
            <rect x="160" y="65" width="80" height="90" rx="4" fill="#FFFFFF" stroke="#1F64E7" strokeWidth="4" />
            <path d="M160 85 L180 65 L220 65 L240 85" fill="none" stroke="#1F64E7" strokeWidth="4" strokeLinejoin="round" />
            <circle cx="188" cy="92" r="4" fill="none" stroke="#1F64E7" strokeWidth="4" />
            <circle cx="212" cy="92" r="4" fill="none" stroke="#1F64E7" strokeWidth="4" />
            <path d="M188 92 C188 112, 212 112, 212 92" fill="none" stroke="#1F64E7" strokeWidth="4" strokeLinecap="round" />
          </svg>

          {/* Texts */}
          <h3 style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 700,
            fontSize: "20px",
            color: "#1B2559",
            margin: "24px 0 8px 0",
            textAlign: "center"
          }}>Sin resultados</h3>
          <p style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            color: "#687377",
            margin: "0 0 28px 0",
            textAlign: "center"
          }}>No tienes suscripciones agregadas</p>

          {/* Button */}
          <button
            onClick={() => {
              if (isExpired) return;
              openAddDialog();
            }}
            disabled={isExpired}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "259px",
              height: "54px",
              background: "#396BE9",
              borderRadius: "16px",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              color: "#FFFFFF",
              transition: "opacity 0.15s ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 0.9}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Agregar suscripción
          </button>
        </div>
      ) : (
        <>
          {/* ══════ KPI CARDS ══════ */}
          <div style={{ display: "flex", gap: "24px", marginBottom: "32px", flexWrap: "wrap" }}>
            {/* Suscripciones activas */}
            <div style={{
              boxSizing: "border-box", display: "flex", flexDirection: "row", alignItems: "center",
              padding: "9px 30px", gap: "30px", flex: "1 1 300px", height: "123px",
              background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)", borderRadius: "20px",
            }}>
              <div style={{
                width: "80px", height: "80px", borderRadius: "50%", background: "#F0FDFD",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <img src="/Iconos/Suscripciones/suscripciones_activas.png" alt="Suscripciones activas" style={{ width: "49.23px", height: "49.23px", objectFit: "contain" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "4px" }}>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: "25px", color: "#47484C", textAlign: "left" }}>
                  Suscripciones activas
                </span>
                <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "24px", lineHeight: "32px", color: "#255E99", textAlign: "left" }}>
                  {subscriptions.length}
                </div>
              </div>
            </div>

            {/* Total mensual */}
            <div style={{
              boxSizing: "border-box", display: "flex", flexDirection: "row", alignItems: "center",
              padding: "9px 30px", gap: "30px", flex: "1 1 300px", height: "123px",
              background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)", borderRadius: "20px",
            }}>
              <div style={{
                width: "80px", height: "80px", borderRadius: "50%", background: "#F0FDFD",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <img src="/Iconos/Suscripciones/total_suscripciones.png" alt="Total suscripciones" style={{ width: "49.23px", height: "49.23px", objectFit: "contain" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "4px" }}>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: "25px", color: "#47484C", textAlign: "left" }}>
                  Total suscripciones
                </span>
                <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "24px", lineHeight: "32px", color: "#255E99", textAlign: "left" }}>
                  {fmt(totalMonthly, userCurrency)}
                </div>
              </div>
            </div>
          </div>

          {/* ══════ SUBSCRIPTION CARDS GRID ══════ */}
          <div className="aorra-subscriptions-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24, alignItems: "stretch",
          }}>
            {subscriptions.map((sub) => {
              const overdue = isOverdue(sub.next_billing);
              const expiring = isExpiringSoon(sub.next_billing);
              const paymentPending = isPaymentPending(sub.next_billing);
              const dateLabel = formatPaymentDate(sub.next_billing);
              const isRed = overdue || expiring;

              return (
                <div key={sub.id} style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  padding: 24, gap: 16, width: "100%",
                  background: "#FFFFFF", boxShadow: "2px 8px 20px rgba(32,85,95,0.2)",
                  borderRadius: 16, boxSizing: "border-box",
                }}>
                  {/* Header: Icon + Name */}
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 12, width: "100%" }}>
                    <ServiceIcon sub={sub} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 18,
                        lineHeight: "25px", color: "#4A4A4A", overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{sub.name}</div>
                      <div style={{
                        fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: 14,
                        lineHeight: "19px", color: "#687377",
                      }}>
                        {sub.billing_cycle === "yearly" ? "Plan anual" : "Plan mensual"} ⭐
                      </div>
                    </div>
                    {/* Edit/Delete button */}
                    <button
                      onClick={() => openEditDialog(sub)}
                      style={{
                        background: "none", border: "none", cursor: "pointer", padding: 4,
                        color: "#687377", fontSize: 18, flexShrink: 0,
                      }}
                      title="Editar"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
                      </svg>
                    </button>
                  </div>

                  {/* Price section */}
                  <div style={{ width: "100%" }}>
                    <div style={{
                      fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: 14,
                      lineHeight: "19px", color: "#687377",
                    }}>Valor mensual</div>
                    <div style={{
                      fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 20,
                      lineHeight: "27px", color: "#4A4A4A",
                    }}>{fmt(sub.price, userCurrency)}</div>
                  </div>

                  {/* Date badge */}
                  {dateLabel && (
                    <div style={{
                      display: "flex", flexDirection: "row", alignItems: "center",
                      padding: "14px 16px", gap: 8, width: "100%",
                      background: isRed ? "#FFE7E8" : "#F5FAFF",
                      borderRadius: 16, boxSizing: "border-box",
                    }}>
                      {/* Calendar icon */}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={isRed ? "#FF0040" : "#1648CE"}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none" stroke={isRed ? "#FF0040" : "#1648CE"} strokeWidth="2"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke={isRed ? "#FF0040" : "#1648CE"} strokeWidth="2" strokeLinecap="round"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke={isRed ? "#FF0040" : "#1648CE"} strokeWidth="2" strokeLinecap="round"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke={isRed ? "#FF0040" : "#1648CE"} strokeWidth="2"/>
                      </svg>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: 13,
                        lineHeight: "18px", color: isRed ? "#FF0040" : "#2B50AF",
                      }}>
                        {overdue ? `Vencido 😨: ${dateLabel}` : `Vence: ${dateLabel}`}
                      </span>
                    </div>
                  )}

                  {/* ─── Pay subscription button (matches CreditCardsPage style) ─── */}
                  <button type="button" onClick={() => setHistorySub(sub)}
                    className="w-full rounded-xl border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700">
                    Historial de pagos
                  </button>
                  {paymentPending && <button
                    onClick={(e) => { e.stopPropagation(); if (!isExpired) openPaymentModal(sub); }}
                    disabled={isExpired}
                    style={{
                      width: "100%",
                      height: "42px",
                      background: "#396BE9",
                      borderRadius: "16px",
                      border: "none",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 500,
                      fontSize: "16px",
                      color: "#FFFFFF",
                      cursor: isExpired ? "not-allowed" : "pointer",
                      opacity: isExpired ? 0.5 : 1,
                      transition: "opacity 0.15s ease",
                    }}
                    onMouseEnter={(e) => { if (!isExpired) e.currentTarget.style.opacity = "0.85"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = isExpired ? "0.5" : "1"; }}
                  >
                    Pagar suscripción
                  </button>}
                </div>
              );
            })}

            {/* ═══ Add New Subscription Card (inline when < 3 subs) ═══ */}
            {subscriptions.length < 3 && (
              <div
                onClick={() => { if (!isExpired) openAddDialog(); }}
                style={{
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "24px 19px 20px",
                  gap: "20px",
                  width: "100%",
                  minHeight: "280px",
                  background: "transparent",
                  border: "2px dashed rgba(57, 107, 233, 0.4)",
                  borderRadius: "16px",
                  cursor: isExpired ? "not-allowed" : "pointer",
                  opacity: isExpired ? 0.5 : 1,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={e => {
                  if (!isExpired) {
                    e.currentTarget.style.background = "#FFFFFF";
                    e.currentTarget.style.borderColor = "#396BE9";
                    e.currentTarget.style.boxShadow = "2px 8px 20px rgba(32, 85, 95, 0.15)";
                  }
                }}
                onMouseLeave={e => {
                  if (!isExpired) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "rgba(57, 107, 233, 0.4)";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
              >
                <div style={{
                  width: "80px",
                  height: "80px",
                  background: "#E1E9FC",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="3" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
                <div style={{ textAlign: "center" }}>
                  <p style={{
                    margin: 0,
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 800,
                    fontSize: "18px",
                    color: "#4A4A4A",
                  }}>Agregar nueva suscripción</p>
                  <p style={{
                    margin: "4px 0 0 0",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#687377",
                  }}>Haz clic para comenzar</p>
                </div>
              </div>
            )}
          </div>

          {/* ═══ Add New Subscription Bar (horizontal when >= 3 subs) ═══ */}
          {subscriptions.length >= 3 && (
            <div
              onClick={() => { if (!isExpired) openAddDialog(); }}
              style={{
                display: "flex", flexDirection: "row", justifyContent: "center",
                alignItems: "center", padding: "16px 24px", gap: 12,
                width: "100%", marginTop: 32,
                background: "transparent",
                border: "2px dashed rgba(57, 107, 233, 0.4)",
                borderRadius: 16, boxSizing: "border-box",
                cursor: isExpired ? "not-allowed" : "pointer",
                opacity: isExpired ? 0.5 : 1,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={e => {
                if (!isExpired) {
                  e.currentTarget.style.background = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#396BE9";
                  e.currentTarget.style.boxShadow = "2px 8px 20px rgba(32, 85, 95, 0.15)";
                }
              }}
              onMouseLeave={e => {
                if (!isExpired) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = "rgba(57, 107, 233, 0.4)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              <div style={{
                width: "36px",
                height: "36px",
                background: "#E1E9FC",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="3" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
              <span style={{
                fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 18,
                lineHeight: "25px", color: "#4A4A4A", textAlign: "center",
              }}>Agregar nueva suscripción</span>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════
          FORM DIALOG — Figma design with 3D icon selector
      ══════════════════════════════════════════ */}
      {dialogOpen && (
        <>
          {/* Backdrop */}
          <div onClick={() => setDialogOpen(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9998,
          }} />
          {/* Modal */}
          <div style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center",
            padding: "24px 28px", gap: 14, width: 480, maxWidth: "calc(100vw - 40px)",
            background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)",
            borderRadius: 20,
          }}>

            {/* ─── Header row ─── */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", width: "100%", position: "relative" }}>
              <h2 style={{
                margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700,
                fontSize: 20, lineHeight: "28px", color: "#1B2559", textAlign: "center",
              }}>{editingSub ? "Editar suscripción" : "Agregar suscripción"}</h2>
              <button onClick={() => setDialogOpen(false)} style={{
                position: "absolute", right: -4, top: -4,
                background: "none", border: "none", cursor: "pointer", padding: 4,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#255E99" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* ─── Selected icon preview (dashed circle) ─── */}
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              border: "1px dashed #4A4A4A", display: "flex",
              alignItems: "center", justifyContent: "center",
              overflow: "hidden", flexShrink: 0,
            }}>
              {formData.icon && formData.icon.startsWith("/") ? (
                <img src={formData.icon} alt="Icono" style={{ width: 56, height: 56, objectFit: "contain" }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              )}
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>

              {/* ─── Name + Date row ─── */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 2 }}>
                  <label style={labelStyle}>Nombre</label>
                  <input type="text" placeholder="Ej: Netflix Premium" value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    style={inputStyle} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={labelStyle}>Próximo cobro</label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        style={{
                          ...inputStyle,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "#FFFFFF",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <span style={{ color: formData.next_billing ? "#4A4A4A" : "#A89E9E", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {formData.next_billing 
                            ? new Date(formData.next_billing + "T12:00:00").toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : "dd/mm/aaaa"}
                        </span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#255E99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-2xl shadow-2xl border-0 overflow-hidden" align="end" style={{ background:"rgba(255,255,255,0.98)", backdropFilter:"blur(10px)", zIndex: 10000 }}>
                      <Calendar
                        mode="single"
                        defaultMonth={formData.next_billing ? new Date(formData.next_billing + "T12:00:00") : new Date()}
                        onSelect={(date) => {
                          if (date) {
                            const yyyy = date.getFullYear();
                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                            const dd = String(date.getDate()).padStart(2, '0');
                            setFormData({ ...formData, next_billing: `${yyyy}-${mm}-${dd}` });
                          } else {
                            setFormData({ ...formData, next_billing: "" });
                          }
                          setDatePickerOpen(false);
                        }}
                        numberOfMonths={1}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* ─── Price + Cycle row ─── */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={labelStyle}>Precio mensual</label>
                  <CurrencyInput
                    currency={formData.currency || userCurrency}
                    placeholder="0"
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    style={{ ...inputStyle, paddingLeft: 34 }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={labelStyle}>Ciclo de pago</label>
                  <select value={formData.billing_cycle}
                    onChange={e => setFormData({ ...formData, billing_cycle: e.target.value })}
                    style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}>
                    <option value="monthly">Mensual</option>
                    <option value="yearly">Anual</option>
                  </select>
                </div>
              </div>

              {/* ─── Icon selector grid ─── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={labelStyle}>Selecciona un icono</label>
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8,
                }}>
                  {ICON_OPTIONS.map(icon => (
                    <button key={icon.src} type="button" onClick={() => setFormData({ ...formData, icon: icon.src })}
                      style={{
                        boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 8, width: "100%", aspectRatio: "1",
                        background: formData.icon === icon.src ? "rgba(225,233,252,1)" : "rgba(225,233,252,0.73)",
                        border: formData.icon === icon.src ? "2px solid #396BE9" : "1px solid rgba(225,233,252,0.73)",
                        borderRadius: 16, cursor: "pointer",
                        transition: "all 0.15s",
                      }}>
                      <img src={icon.src} alt={icon.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* ─── Buttons ─── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
                <button type="submit" disabled={submitting} style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "100%", height: 44, background: "#396BE9", borderRadius: 16,
                  border: "none", cursor: submitting ? "wait" : "pointer",
                  fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15,
                  color: "#FFFFFF", opacity: submitting ? 0.7 : 1, transition: "opacity 0.15s",
                }}>
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingSub ? "Guardar cambios" : "Crear suscripción")}
                </button>

                {editingSub && !isExpired && (
                  <span
                    onClick={() => { if (window.confirm("¿Eliminar esta suscripción?")) handleDelete(editingSub.id); }}
                    style={{
                      fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14,
                      color: "#FF0040", cursor: "pointer", textAlign: "center",
                      marginTop: 4,
                    }}
                  >
                    Eliminar suscripción
                  </span>
                )}
              </div>
            </form>
          </div>
        </>
      )}

      {/* ══════ PAYMENT MODAL (same style as CreditCardsPage) ══════ */}
      {paymentModalSub && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setPaymentModalSub(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9998 }}
          />
          {/* Dialog */}
          <div style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            zIndex: 9999,
            width: "460px", maxWidth: "calc(100vw - 32px)",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
            padding: "24px",
            boxSizing: "border-box",
          }}>

            {/* Close X */}
            <button
              type="button"
              onClick={() => setPaymentModalSub(null)}
              style={{ position: "absolute", top: "14px", right: "14px", width: "24px", height: "24px", border: "none", background: "none", cursor: "pointer", padding: 0 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <line x1="6" y1="6" x2="18" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
                <line x1="18" y1="6" x2="6" y2="18" stroke="#255E99" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", width: "100%" }}>

              {/* Title */}
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700, fontSize: "20px", lineHeight: "28px",
                color: "#1B2559", textAlign: "center", margin: 0,
              }}>Pagar — {paymentModalSub.name}</h2>

              {/* Service icon circle (teal bg like CreditCards) */}
              <div style={{
                width: "80px", height: "80px", borderRadius: "100px",
                background: "#DBFBF8",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <ServiceIcon sub={paymentModalSub} />
              </div>

              {/* Precio actual box (mirrors Deuda actual from CreditCards) */}
              <div style={{
                boxSizing: "border-box",
                display: "flex", flexDirection: "row", alignItems: "center",
                padding: "0px 20px", justifyContent: "space-between",
                width: "100%", height: "53px",
                border: "2px solid #CDF7F7", borderRadius: "16px",
              }}>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "16px", color: "#5C5C5C" }}>Precio mensual</span>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "20px", color: "#4A4A4A" }}>
                  {fmt(paymentModalSub.price, paymentModalSub.currency || userCurrency)}
                </span>
              </div>

              {/* Form fields */}
              <form onSubmit={handlePaymentSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>

                <label className="text-sm w-full block">Medio de pago
                  <select aria-label="Medio de pago" className="w-full border rounded-xl p-2" value={paymentData.funding_type || "cash"}
                    onChange={e => setPaymentData({ ...paymentData, funding_type: e.target.value, payment_method: "", account_id: "unassigned" })}>
                    <option value="cash">Cuenta o efectivo</option>
                    <option value="credit">Tarjeta de crédito</option>
                  </select>
                </label>
                {paymentData.funding_type === "credit" ? <>
                  <label className="text-sm w-full block">Tarjeta de crédito
                    <select aria-label="Tarjeta de crédito" className="w-full border rounded-xl p-2" value={paymentData.payment_method || ""}
                      disabled={paymentCardsLoading || paymentCardsError} onChange={e => setPaymentData({ ...paymentData, payment_method: e.target.value })}>
                      <option value="">Selecciona una tarjeta</option>
                      {paymentCards.map(card => <option key={card.id} value={card.id}>{card.name} · {card.currency}</option>)}
                    </select>
                  </label>
                  <p className="text-sm">Este cargo aumenta la deuda de tu tarjeta. Tu dinero disponible no cambia.</p>
                  {paymentCardsError && <p role="alert">No se pudieron cargar las tarjetas. Cierra y vuelve a abrir el pago.</p>}
                  {!paymentCardsLoading && !paymentCardsError && !paymentCards.length && <p role="alert">Agrega primero tu tarjeta en Cuentas y tarjetas.</p>}
                </> : <AccountSelect value={paymentData.account_id} onChange={account_id => setPaymentData({ ...paymentData, account_id })} />}
                {/* Monto a pagar */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Monto a pagar</label>
                  <CurrencyInput
                    currency={paymentData.currency || paymentModalSub.currency || userCurrency}
                    placeholder={paymentModalSub.price ? String(paymentModalSub.price) : "0"}
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    autoFocus
                    style={{
                      boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px 10px 34px",
                      border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                      fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                    }}
                  />
                </div>

                {/* Nota */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "14px", lineHeight: "20px", color: "#5C5C5C" }}>Nota (opcional)</label>
                  <input
                    type="text"
                    placeholder={`Ej: Pago ${paymentModalSub.name} julio`}
                    value={paymentData.description}
                    onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
                    style={{
                      boxSizing: "border-box", width: "100%", height: "42px", padding: "10px 12px",
                      border: "1px solid #DDDDDD", borderRadius: "20px", outline: "none",
                      fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: "15px", color: "#4A4A4A",
                    }}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={paymentSubmitting}
                  style={{
                    width: "100%", height: "46px",
                    background: "#396BE9", borderRadius: "16px", border: "none",
                    fontFamily: "'Manrope', sans-serif", fontWeight: 500, fontSize: "15px",
                    color: "#FFFFFF",
                    cursor: paymentSubmitting ? "not-allowed" : "pointer",
                    opacity: paymentSubmitting ? 0.7 : 1,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    transition: "opacity 0.15s",
                    marginTop: "4px",
                  }}
                >
                  {paymentSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Pago"}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {historySub && <SubscriptionPaymentHistory subscription={historySub}
        onClose={() => setHistorySub(null)} onChanged={fetchSubscriptions} readOnly={isExpired} />}
      <style>{`
        @media (max-width: 900px) {
          .aorra-subscriptions-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

/* ─── 3D Icon Options ─── */
const ICON_OPTIONS = [
  { src: "/Iconos/Suscripciones/iconos/04262950ffda9749685a693bccf196e4b77ee329.png", label: "Calendario" },
  { src: "/Iconos/Suscripciones/iconos/24c777faa6d51f63c577df0c82ef843fd98d3243.png", label: "Cámara" },
  { src: "/Iconos/Suscripciones/iconos/3220cefa4635aa5520d029268493693a9c77e5af.png", label: "Estrella" },
  { src: "/Iconos/Suscripciones/iconos/39768f72875cbe16732120830f3f1b7db5b88d65.png", label: "Gráfico" },
  { src: "/Iconos/Suscripciones/iconos/916bd3530d8fb4bcb87d644bdeab3cbede55eb89.png", label: "Maleta" },
  { src: "/Iconos/Suscripciones/iconos/a59366e1f86a701e417d99c5fde16a10a77bf64d_1.png", label: "Alcancía" },
  { src: "/Iconos/Suscripciones/iconos/ca416ca27c6a31dabeebdd4636c7c6fb9efb155c.png", label: "Dinero" },
  { src: "/Iconos/Suscripciones/iconos/d728e04ee13352650d2bd884551f97d02b8f59df.png", label: "Bombilla" },
  { src: "/Iconos/Suscripciones/iconos/e51ae470423e15a3d555e100dc434123de82ee16_1.png", label: "Billetera" },
  { src: "/Iconos/Suscripciones/iconos/e6ec071d48d819cd364064b332a854ccd00578bf.png", label: "Gamepad" },
  { src: "/Iconos/Suscripciones/iconos/e876e3533c53223e78aa079526a49dfeb2fb09d5.png", label: "Café" },
  { src: "/Iconos/Suscripciones/iconos/eb5abe1d9605bd951b5e73c4e2d18a26282c5d3a.png", label: "Pizza" },
];

/* ─── Shared styles ─── */
const labelStyle = {
  fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 14,
  lineHeight: "20px", color: "#5C5C5C",
};

const inputStyle = {
  boxSizing: "border-box", width: "100%", height: 40,
  background: "#F5F6FA", border: "1px solid #CDE3F3",
  borderRadius: 12, padding: "0 12px", fontFamily: "'Manrope', sans-serif",
  fontWeight: 600, fontSize: 14, color: "#4A4A4A", outline: "none",
};

export default SubscriptionsPage;
